export const MAX_PDF_FILE_SIZE_BYTES = 10 * 1024 * 1024;
export const MAX_PDF_FILE_SIZE_MB = 10;
export const MAX_PDF_PAGE_COUNT = 10;

export class PdfUploadValidationError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'PdfUploadValidationError';
    this.code = code;
  }
}

const defaultPdfLibraryLoader = () => import('pdf-lib');

export const readPdfPageCount = async (file, loadPdfLibrary = defaultPdfLibraryLoader) => {
  const { PDFDocument } = await loadPdfLibrary();
  const data = await file.arrayBuffer();
  const document = await PDFDocument.load(data);
  return document.getPages().length;
};

export const validatePdfForUpload = async (
  file,
  { readPageCount = readPdfPageCount } = {}
) => {
  const hasPdfMimeType = file?.type === 'application/pdf';
  const hasPdfExtension = file?.name?.toLowerCase().endsWith('.pdf');

  if (!file || (!hasPdfMimeType && !hasPdfExtension)) {
    throw new PdfUploadValidationError('invalid_type', 'Please select a PDF file.');
  }

  if (file.size > MAX_PDF_FILE_SIZE_BYTES) {
    throw new PdfUploadValidationError(
      'file_too_large',
      `This PDF is too large. The maximum file size is ${MAX_PDF_FILE_SIZE_MB}MB.`
    );
  }

  let pageCount;
  try {
    pageCount = await readPageCount(file);
  } catch (error) {
    throw new PdfUploadValidationError(
      'invalid_pdf',
      'We could not read this PDF. It may be damaged, encrypted, or password-protected.'
    );
  }

  if (!Number.isInteger(pageCount) || pageCount < 1) {
    throw new PdfUploadValidationError(
      'invalid_pdf',
      'We could not find any readable pages in this PDF.'
    );
  }

  if (pageCount > MAX_PDF_PAGE_COUNT) {
    throw new PdfUploadValidationError(
      'too_many_pages',
      `This PDF has ${pageCount} pages. The maximum is ${MAX_PDF_PAGE_COUNT} pages.`
    );
  }

  return { pageCount };
};
